import Box from '@mui/material/Box';
import { GridColDef, GridSortItem } from '@mui/x-data-grid-pro';
import { useMemo } from 'react';
import { useIntl } from 'react-intl';

import { useAuth } from '@verantos-dev/core-auth';
import { type AnalyticalVariableListItem } from '@verantos-dev/feature-analytical-variables';
import { type DataGridMessages } from '@verantos-dev/shared-components';
import { clickableCellStyle } from '@verantos-dev/shared-styles';
import { formatDate } from '@verantos-dev/shared-utils';

import { AvLibraryRowActions } from '../components/AvLibraryRowActions';

interface UseAvLibraryOptions {
  onNameClick?: (analyticalVariable: AnalyticalVariableListItem) => void;
}

export const useAvLibrary = ({ onNameClick }: UseAvLibraryOptions = {}) => {
  const intl = useIntl();
  // Internal (Verantos) users see AVs across every org, so they get an
  // "Organization" column; external users only ever see their own org's AVs,
  // so the column is omitted.
  const { user } = useAuth();
  const isInternalUser = Boolean(user?.isInternalUser);

  const columns: GridColDef<AnalyticalVariableListItem>[] = useMemo(
    () => [
      {
        field: 'name',
        headerName: intl.formatMessage({ id: 'avLibrary.columns.name' }),
        flex: 2,
        minWidth: 250,
        renderCell: (params) => (
          <Box
            sx={onNameClick ? clickableCellStyle : undefined}
            onClick={() => onNameClick?.(params.row as AnalyticalVariableListItem)}
            data-testid={`av-library-name-${params.row.analyticalVariableId}`}
          >
            {params.row.dataElement || params.row.name}
          </Box>
        ),
      },
      {
        field: 'format',
        headerName: intl.formatMessage({ id: 'avLibrary.columns.format' }),
        flex: 1,
        minWidth: 130,
      },
      {
        field: 'owner',
        headerName: intl.formatMessage({ id: 'avLibrary.columns.owner' }),
        flex: 1,
        minWidth: 150,
      },
      // Internal-only column. ORGANIZATION_NAME is resolved server-side but is
      // not a column on the AV table, so it can't be server-sorted — mark it
      // non-sortable rather than expose a dead sort affordance.
      ...(isInternalUser
        ? [
            {
              field: 'organizationName',
              headerName: intl.formatMessage({ id: 'avLibrary.columns.organization' }),
              flex: 1,
              minWidth: 180,
              sortable: false,
              renderCell: (params) => <Box>{params.row.organizationName ?? '--'}</Box>,
            } satisfies GridColDef<AnalyticalVariableListItem>,
          ]
        : []),
      {
        // Server-mode sort key: the backend maps this field to a column via
        // ``camel_to_column`` (``createdAt`` → ``CREATED_AT``). It must name the
        // real timestamp column, NOT ``createdBy`` (the composite audit object,
        // which has no DB column — sorting on it was a silent no-op). The
        // displayed value is ``createdBy.transactionDate``, which IS
        // ``CREATED_AT``, so sorting on ``createdAt`` matches what's shown.
        field: 'createdAt',
        headerName: intl.formatMessage({ id: 'avLibrary.columns.updatedAt' }),
        type: 'dateTime',
        minWidth: 250,
        flex: 0.5,
        valueGetter: (_: unknown, row: AnalyticalVariableListItem) => {
          return row.createdBy?.transactionDate ? new Date(row.createdBy.transactionDate) : null;
        },
        renderCell: (params) => {
          const formatted = params.row.createdBy?.transactionDate
            ? formatDate(params.row.createdBy.transactionDate, 'dateTimeFull')
            : '--';
          return <Box>{formatted}</Box>;
        },
      },
      // OR-6090: creator-only Rename. Renders nothing for non-owner rows, so
      // the column reads as empty rather than showing a dead kebab.
      {
        field: 'actions',
        headerName: '',
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        width: 56,
        align: 'right',
        renderCell: (params) => <AvLibraryRowActions analyticalVariable={params.row} />,
      },
    ],
    [intl, onNameClick, isInternalUser]
  );

  const sortModel: GridSortItem[] = [{ field: 'name', sort: 'asc' }];

  const getRowId = (row: AnalyticalVariableListItem) => row.analyticalVariableId;

  // Custom empty-state copy (mirrors the projects list) so an empty library
  // reads "No analytical variables available…" instead of the generic "No rows".
  const messages: DataGridMessages = useMemo(
    () => ({
      noRows: intl.formatMessage({ id: 'avLibrary.empty' }),
      noResults: intl.formatMessage({ id: 'avLibrary.empty' }),
    }),
    [intl]
  );

  return {
    columns,
    getRowId,
    sortModel,
    messages,
  };
};
